import intl from "react-intl-universal";
import {
  formatFilterAmount,
  formatFilterDate,
  parseAmountInput,
  startOfDay,
  endOfDay,
} from "../../../../Components/Molecules/GridHistory/gridFilterUtils";
import {
  BAAS_TRANSACTION_STATUSES,
  BAAS_TRANSACTION_TYPES,
  getBaasTransactionEntities,
} from "../../../../Services/baasTransactionsApi";

export const EMPTY_BAAS_TRANSACTIONS_FILTERS = {
  searchQuery: "",
  dateFrom: null,
  dateTo: null,
  amountMin: "",
  amountMax: "",
  types: [],
  statuses: [],
  entityIds: [],
  instanceIds: [],
  exceptionsOnly: false,
  pendingPixOnly: false,
};

/** Catalog / statement instance ids may be numeric or `19:production`. */
export const baasTransactionInstanceKey = (value) => {
  if (value == null || value === "") return "";
  const numeric = Number(value);
  if (Number.isFinite(numeric)) return String(numeric);
  const prefix = String(value).split(":")[0];
  const fromPrefix = Number(prefix);
  return Number.isFinite(fromPrefix) ? String(fromPrefix) : String(value);
};

export const getBaasTransactionInstanceOptions = (instances = [], selectedInstance = null) => {
  const env = selectedInstance?.environment;
  const seen = new Set();
  return (instances || [])
    .filter((row) => !env || !row?.environment || row.environment === env)
    .map((row) => {
      const id = baasTransactionInstanceKey(row?.baasInstanceId ?? row?.instanceId ?? row?.id);
      const label = String(row?.displayName || row?.name || id).trim();
      return { id, label };
    })
    .filter((row) => {
      if (!row.id || seen.has(row.id)) return false;
      seen.add(row.id);
      return true;
    })
    .sort((a, b) => a.label.localeCompare(b.label));
};

/** Initial Transações filters — Data chip = today (start=end). */
export const createDefaultBaasTransactionsFilters = () => {
  const today = startOfDay(new Date());
  return {
    ...EMPTY_BAAS_TRANSACTIONS_FILTERS,
    dateFrom: today,
    dateTo: today,
  };
};

export const getBaasTransactionTypeOptions = () =>
  BAAS_TRANSACTION_TYPES.map((type) => ({
    id: type,
    label: intl.get(`baas.transactions.type.${type}`),
  }));

export const getBaasTransactionStatusOptions = () =>
  BAAS_TRANSACTION_STATUSES.map((status) => ({
    id: status,
    label: intl.get(`baas.transactions.status.${status}`),
  }));

export const getBaasTransactionEntityOptions = (transactions = []) =>
  getBaasTransactionEntities(transactions).map((entity) => ({
    id: entity.id,
    label: entity.name,
  }));

const transactionEntityId = (transaction) =>
  transaction?.entityId || transaction?.clientId || "";

const transactionEntityName = (transaction) =>
  transaction?.entityName || transaction?.clientName || "";

const matchesSearch = (transaction, query) => {
  if (!query?.trim()) return true;
  const normalized = query.trim().toLowerCase();
  return [
    transaction.referenceId,
    transaction.id,
    transaction.description,
    transactionEntityName(transaction),
    transactionEntityId(transaction),
    transaction.counterpartyName,
    transaction.counterpartyDocument,
    transaction.instanceName,
    transaction.type,
    transaction.rail,
  ]
    .filter(Boolean)
    .some((value) => String(value).toLowerCase().includes(normalized));
};

const matchesDateRange = (transaction, dateFrom, dateTo) => {
  if (!dateFrom && !dateTo) return true;
  const createdAt = new Date(transaction.createdAt);
  if (Number.isNaN(createdAt.getTime())) return false;
  if (dateFrom && createdAt < startOfDay(dateFrom)) return false;
  if (dateTo && createdAt > endOfDay(dateTo)) return false;
  return true;
};

const matchesAmountRange = (transaction, amountMin, amountMax) => {
  const minMajor = parseAmountInput(amountMin);
  const maxMajor = parseAmountInput(amountMax);
  // UI stores amounts in cents (Carteira formatCurrency convention).
  const amountCents = Math.abs(Number(transaction.amount) || 0);
  if (minMajor != null && amountCents < Math.round(minMajor * 100)) return false;
  if (maxMajor != null && amountCents > Math.round(maxMajor * 100)) return false;
  return true;
};

const matchesRail = (transaction, types) => {
  if (!types?.length) return true;
  const rail = String(transaction.rail || transaction.type || "").toUpperCase();
  return types.some((type) => String(type).toUpperCase() === rail);
};

export const filterBaasTransactions = (items = [], filters = EMPTY_BAAS_TRANSACTIONS_FILTERS) =>
  items.filter((transaction) => {
    if (!matchesSearch(transaction, filters.searchQuery)) return false;
    if (!matchesDateRange(transaction, filters.dateFrom, filters.dateTo)) return false;
    if (!matchesAmountRange(transaction, filters.amountMin, filters.amountMax)) return false;
    if (!matchesRail(transaction, filters.types)) return false;
    if (filters.statuses?.length && !filters.statuses.includes(transaction.status)) return false;
    if (
      filters.entityIds?.length &&
      !filters.entityIds.includes(transactionEntityId(transaction))
    ) {
      return false;
    }
    if (filters.instanceIds?.length) {
      const allowed = new Set(filters.instanceIds.map(baasTransactionInstanceKey));
      if (!allowed.has(baasTransactionInstanceKey(transaction.instanceId))) {
        return false;
      }
    }
    if (
      filters.exceptionsOnly &&
      transaction.status !== "rejected" &&
      !transaction.refunded &&
      !transaction.returned
    ) {
      return false;
    }
    if (filters.pendingPixOnly && !transaction.pendingPix) {
      return false;
    }
    return true;
  });

export const hasActiveBaasTransactionsFilters = (filters = EMPTY_BAAS_TRANSACTIONS_FILTERS) =>
  Boolean(
    filters.searchQuery?.trim() ||
      filters.dateFrom ||
      filters.dateTo ||
      filters.amountMin ||
      filters.amountMax ||
      filters.types?.length ||
      filters.statuses?.length ||
      filters.entityIds?.length ||
      filters.instanceIds?.length ||
      filters.exceptionsOnly ||
      filters.pendingPixOnly,
  );

export const isBaasTransactionsChipFilterActive = (filters, chipId) => {
  switch (chipId) {
    case "date":
      return Boolean(filters.dateFrom || filters.dateTo);
    case "amount":
      return Boolean(filters.amountMin || filters.amountMax);
    case "type":
      return Boolean(filters.types?.length);
    case "status":
      return Boolean(filters.statuses?.length);
    case "entity":
      return Boolean(filters.entityIds?.length);
    case "instance":
      return Boolean(filters.instanceIds?.length);
    case "pending":
      return Boolean(filters.pendingPixOnly);
    default:
      return false;
  }
};

export const buildBaasTransactionsFilterTags = (
  filters = EMPTY_BAAS_TRANSACTIONS_FILTERS,
  transactions = [],
  instanceOptions = [],
) => {
  const tags = [];
  const typeOptions = getBaasTransactionTypeOptions();
  const statusOptions = getBaasTransactionStatusOptions();
  const entityOptions = getBaasTransactionEntityOptions(transactions);

  if (filters.searchQuery?.trim()) {
    tags.push({
      key: "searchQuery",
      iconType: "search",
      value: filters.searchQuery.trim(),
    });
  }

  if (filters.dateFrom || filters.dateTo) {
    const fromLabel = filters.dateFrom ? formatFilterDate(filters.dateFrom) : "…";
    const toLabel = filters.dateTo ? formatFilterDate(filters.dateTo) : "…";
    tags.push({
      key: "date",
      iconType: "date",
      value: `${fromLabel} – ${toLabel}`,
    });
  }

  if (filters.amountMin || filters.amountMax) {
    const minLabel = filters.amountMin ? formatFilterAmount(filters.amountMin) : "…";
    const maxLabel = filters.amountMax ? formatFilterAmount(filters.amountMax) : "…";
    tags.push({
      key: "amount",
      iconType: "amount",
      value: `${minLabel} – ${maxLabel}`,
    });
  }

  if (filters.types?.length) {
    tags.push({
      key: "types",
      iconType: "type",
      value: filters.types
        .map((typeId) => typeOptions.find((item) => item.id === typeId)?.label ?? typeId)
        .join(", "),
    });
  }

  if (filters.statuses?.length) {
    tags.push({
      key: "statuses",
      iconType: "status",
      value: filters.statuses
        .map((statusId) => statusOptions.find((item) => item.id === statusId)?.label ?? statusId)
        .join(", "),
    });
  }

  if (filters.entityIds?.length) {
    tags.push({
      key: "entityIds",
      iconType: "subAccount",
      value: filters.entityIds
        .map((entityId) => entityOptions.find((item) => item.id === entityId)?.label ?? entityId)
        .join(", "),
    });
  }

  if (filters.instanceIds?.length) {
    tags.push({
      key: "instanceIds",
      iconType: "subAccount",
      value: filters.instanceIds
        .map((instanceId) => {
          const key = baasTransactionInstanceKey(instanceId);
          return instanceOptions.find((item) => item.id === key)?.label ?? key;
        })
        .join(", "),
    });
  }

  if (filters.pendingPixOnly) {
    tags.push({
      key: "pendingPixOnly",
      iconType: "status",
      value: intl.get("baas.transactions.filterPending"),
    });
  }

  return tags;
};

export const removeBaasTransactionsFilterTag = (filters, tagKey) => {
  switch (tagKey) {
    case "searchQuery":
      return { ...filters, searchQuery: "" };
    case "date":
      return { ...filters, dateFrom: null, dateTo: null };
    case "amount":
      return { ...filters, amountMin: "", amountMax: "" };
    case "types":
      return { ...filters, types: [] };
    case "statuses":
      return { ...filters, statuses: [] };
    case "entityIds":
      return { ...filters, entityIds: [] };
    case "instanceIds":
      return { ...filters, instanceIds: [] };
    case "pendingPixOnly":
      return { ...filters, pendingPixOnly: false };
    default:
      return filters;
  }
};
